// learmo-modal.service.ts

import { Injectable, TemplateRef, Type } from '@angular/core';
import { Observable } from 'rxjs';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { GlobalLearmoModalComponent } from '../partials/global-learmo-modal/global-learmo-modal.component';
import {
  CustomNgbModalService,
  LearmoModalOptions,
} from './custom-ngbmodal.service';

/**
 * Abstracted Modal Reference to decouple from NgbModalRef
 */
export class LearmoModalRef {
  constructor(private ngbRef: NgbModalRef) {}

  get componentInstance(): any {
    const instance = this.ngbRef.componentInstance;
    if (
      instance instanceof GlobalLearmoModalComponent &&
      instance.innerComponentInstance
    ) {
      return instance.innerComponentInstance;
    }
    return instance;
  }

  get result(): Promise<any> {
    return this.ngbRef.result;
  }

  get closed(): Observable<any> {
    return this.ngbRef.closed;
  }

  get dismissed(): Observable<any> {
    return this.ngbRef.dismissed;
  }

  close(result?: any): void {
    this.ngbRef.close(result);
  }

  dismiss(reason?: any): void {
    this.ngbRef.dismiss(reason);
  }

  /**
   * Helper to update the Unified Shell state safely
   */
  setTitle(title: string): void {
    if (this.ngbRef.componentInstance instanceof GlobalLearmoModalComponent) {
      this.ngbRef.componentInstance.title = title;
    }
  }

  setIcon(icon: string): void {
    if (this.ngbRef.componentInstance instanceof GlobalLearmoModalComponent) {
      this.ngbRef.componentInstance.iconIcon = icon;
    }
  }

  setLoading(loading: boolean): void {
    if (this.ngbRef.componentInstance instanceof GlobalLearmoModalComponent) {
      this.ngbRef.componentInstance.loading = loading;
    }
  }
}

/**
 * Unified Modal Service - The only modal service that should be used in the app.
 * Abstraction layer over NgbModal.
 */
@Injectable({
  providedIn: 'root',
})
export class LearmoModalService {
  constructor(private ngbModal: NgbModal) {}

  open(content: any, options: LearmoModalOptions = {}): LearmoModalRef {
    // Default to premium if not specified
    if (options.premium === undefined) {
      options.premium = true;
    }

    // Ensure the modal is attached to the body by default
    if (options.container === undefined) {
      options.container = 'body';
    }

    // Apply appropriate windowClass based on modal type
    const windowClasses: string[] = [];

    if (options.windowClass) {
      windowClasses.push(options.windowClass);
    }

    // Add fullscreen class if isFullscreen is true
    if (options.size === 'fullscreen') {
      windowClasses.push('learmo-full-screen-modal');
    }
    // Add full-height class if fullHeight is true (but not fullscreen)
    else if (options.fullHeight) {
      windowClasses.push('learmo-full-height-modal');
    }

    // Update the options with combined window classes
    if (windowClasses.length > 0) {
      options.windowClass = windowClasses.join(' ');
    }

    const ref = this.ngbModal.open(content, options);
    return new LearmoModalRef(ref);
  }
}
